import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CANDIDATE_DOCUMENT_LABELS, CANDIDATE_DOCUMENT_TYPES } from "../config/candidateDocumentTypes";
import {
    deleteCandidateDocumentDraftFile,
    getPublicCandidateDocumentsForm,
    openCandidateDocumentsUploadSession,
    submitPublicCandidateDocuments,
    uploadCandidateDocumentDraftFile,
    validateCandidateDocumentDraftFile,
} from "../services/candidateDocumentsApi";
import {
    MAX_CANDIDATE_DOCUMENT_FILES,
    validateCandidateDocumentFile,
} from "../utils/candidateDocumentsDraft";
import {
    clearCandidateDocumentSession, readCandidateDocumentSession, saveCandidateDocumentSession,
} from "../utils/candidateDocumentsSession";

function formatFileSize(bytes) {
    const value = Number(bytes || 0);
    if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} КБ`;
    return `${(value / 1024 / 1024).toFixed(1)} МБ`;
}

function PageMark({ tone = "gold" }) {
    const palette = tone === "success"
        ? "bg-emerald-100 text-emerald-700"
        : "bg-amber-100 text-amber-700";
    return <span aria-hidden="true" className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl ${palette}`}>
        <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 3.75h7.5L19 8.25v12H7z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 3.75v4.5H19M9.5 13h7M9.5 16.5h5" />
        </svg>
    </span>;
}
function readFile(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error(`Не удалось прочитать ${file.name}`));
        reader.readAsDataURL(file);
    });
}

async function fileToUpload(file, kind) {
    validateCandidateDocumentFile(file);
    let preparedFile = file;
    if (file.type.startsWith("image/") && file.type !== "image/gif") {
        const dataUrl = await readFile(file);
        preparedFile = await new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => {
                const scale = Math.min(1, 1600 / Math.max(image.width, image.height));
                const canvas = document.createElement("canvas");
                canvas.width = Math.max(1, Math.round(image.width * scale));
                canvas.height = Math.max(1, Math.round(image.height * scale));
                const context = canvas.getContext("2d");
                if (!context) {
                    reject(new Error(`Не удалось обработать ${file.name}`));
                    return;
                }
                context.drawImage(image, 0, 0, canvas.width, canvas.height);
                canvas.toBlob((blob) => {
                    if (!blob) return reject(new Error(`Не удалось обработать ${file.name}`));
                    resolve(new File([blob], `${file.name.replace(/\.[^.]+$/, "") || "document"}.jpg`, { type: "image/jpeg" }));
                }, "image/jpeg", 0.82);
            };
            image.onerror = () => reject(new Error(`Не удалось прочитать ${file.name}`));
            image.src = dataUrl;
        });
    }
    // Compression normally reduces images, but validate the actual multipart
    // payload as well so an unusual conversion can never exceed the API limit.
    validateCandidateDocumentFile(preparedFile);
    return { kind, file: preparedFile };
}

export default function CandidateDocumentsUploadPage() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [linkChecked, setLinkChecked] = useState(false);
    const [error, setError] = useState("");
    const [entries, setEntries] = useState([]);
    const [kind, setKind] = useState("passport_personal_data");
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deletingIds, setDeletingIds] = useState(() => new Set());
    const [complete, setComplete] = useState(false);
    const [sessionToken, setSessionToken] = useState("");
    const [actualResidenceAddress, setActualResidenceAddress] = useState("");
    const validationControllers = useRef(new Map());

    const validateFile = useCallback(async (item, activeSessionToken) => {
        if (!item?.file_id || !activeSessionToken) return;
        const previous = validationControllers.current.get(item.file_id);
        if (previous) previous.abort();
        const controller = new AbortController();
        validationControllers.current.set(item.file_id, controller);
        setEntries((current) => current.map((row) => row.file_id === item.file_id ? {
            ...row,
            validation_status: "checking",
            validation_message: "Документ отправлен на проверку",
        } : row));
        try {
            const result = await validateCandidateDocumentDraftFile(
                token, activeSessionToken, item.file_id, controller.signal,
            );
            if (validationControllers.current.get(item.file_id) !== controller) return;
            setEntries((current) => current.map((row) => row.file_id === item.file_id ? {
                ...row,
                ...result,
            } : row));
        } catch (validationError) {
            if (controller.signal.aborted || validationError?.name === "AbortError") return;
            if (validationControllers.current.get(item.file_id) !== controller) return;
            setEntries((current) => current.map((row) => row.file_id === item.file_id ? {
                ...row,
                validation_status: validationError?.status === 422 ? "invalid" : "error",
                validation_message: validationError?.message || "Не удалось проверить документ. Повторите попытку",
            } : row));
        } finally {
            if (validationControllers.current.get(item.file_id) === controller) {
                validationControllers.current.delete(item.file_id);
            }
        }
    }, [token]);

    const loadForm = useCallback(async () => {
        setLinkChecked(false);
        setError("");
        try {
            const result = await getPublicCandidateDocumentsForm(token);
            setInfo(result);
            setActualResidenceAddress(result.actual_residence_address || "");
            // Remove Base64 drafts written by the previous implementation.
            try { localStorage.removeItem(`candidate-documents-draft:${token}`); } catch (_) { /* optional */ }
            const saved = readCandidateDocumentSession();
            let draft;
            try {
                draft = await openCandidateDocumentsUploadSession(token, saved);
            } catch (resumeError) {
                if (!saved) throw resumeError;
                clearCandidateDocumentSession();
                draft = await openCandidateDocumentsUploadSession(token);
            }
            const nextToken = draft.session_token || saved;
            if (!nextToken) throw new Error("Сервер не вернул токен загрузочной сессии");
            setSessionToken(nextToken);
            const restoredFiles = draft.files || [];
            setEntries(restoredFiles);
            saveCandidateDocumentSession(nextToken);
            restoredFiles
                .filter((item) => !item.validation_status || ["pending", "checking", "error"].includes(item.validation_status))
                .forEach((item) => { void validateFile(item, nextToken); });
        } catch (loadError) {
            setInfo(null);
            setError(loadError.message);
        } finally {
            setLinkChecked(true);
        }
    }, [token, validateFile]);

    useEffect(() => { loadForm(); }, [loadForm]);
    useEffect(() => () => {
        validationControllers.current.forEach((controller) => controller.abort());
        validationControllers.current.clear();
    }, [token]);

    const totalBytes = useMemo(
        () => entries.reduce((sum, item) => sum + Number(item.size || 0), 0),
        [entries],
    );
    const existingDocuments = useMemo(() => info?.existing_documents || [], [info]);
    const uploadedKinds = useMemo(() => new Set([
        ...existingDocuments.map(item => item.kind),
        ...entries.map(item => item.kind),
    ]), [entries, existingDocuments]);
    const missingRequiredKinds = [
        ...(!uploadedKinds.has("passport") && !uploadedKinds.has("passport_personal_data") ? ["passport_personal_data"] : []),
        ...(!uploadedKinds.has("passport") && !uploadedKinds.has("passport_registration") ? ["passport_registration"] : []),
        ...(!uploadedKinds.has("snils") ? ["snils"] : []),
    ];
    const selectedDocument = CANDIDATE_DOCUMENT_TYPES.find(item => item.value === kind);
    const checkingCount = entries.filter(item => item.validation_status === "checking" || !item.validation_status).length;
    const allDocumentsValid = entries.length > 0 && entries.every(item => item.validation_status === "valid");
    const busy = uploading || submitting || deletingIds.size > 0;

    const addFiles = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";
        if (!info || !files.length) return;
        try {
            setError("");
            if (entries.length + files.length > MAX_CANDIDATE_DOCUMENT_FILES) {
                throw new Error(`Можно приложить не более ${MAX_CANDIDATE_DOCUMENT_FILES} файлов`);
            }
            setUploading(true);
            for (const file of files) {
                const prepared = await fileToUpload(file, kind);
                const uploaded = await uploadCandidateDocumentDraftFile(token, sessionToken, prepared);
                setEntries((current) => [...current, uploaded]);
                void validateFile(uploaded, sessionToken);
            }
        } catch (addError) {
            setError(addError.message);
        } finally {
            setUploading(false);
        }
    };

    const removeFile = async (item) => {
        const controller = validationControllers.current.get(item.file_id);
        if (controller) {
            controller.abort();
            validationControllers.current.delete(item.file_id);
        }
        try {
            setDeletingIds((current) => new Set(current).add(item.file_id));
            setError("");
            await deleteCandidateDocumentDraftFile(token, sessionToken, item.file_id);
            setEntries((current) => current.filter((row) => row.file_id !== item.file_id));
        } catch (removeError) {
            setError(removeError.message);
            void validateFile(item, sessionToken);
        } finally {
            setDeletingIds((current) => {
                const next = new Set(current);
                next.delete(item.file_id);
                return next;
            });
        }
    };

    const submit = async () => {
        if (!actualResidenceAddress.trim()) {
            setError("Укажите фактический адрес проживания");
            return;
        }
        if (!entries.length || missingRequiredKinds.length || !window.confirm("Отправить пакет документов? После отправки изменить его самостоятельно будет нельзя.")) return;
        try {
            setSubmitting(true);
            setError("");
            await submitPublicCandidateDocuments(token, sessionToken, actualResidenceAddress.trim());
            clearCandidateDocumentSession();
            setEntries([]);
            setComplete(true);
        } catch (submitError) {
            setError(submitError.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (complete) {
        return <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-amber-50 p-5">
            <section className="w-full max-w-xl rounded-[2rem] border border-emerald-100 bg-white p-8 text-center shadow-2xl shadow-slate-200/70 md:p-12">
                <div className="flex justify-center"><PageMark tone="success" /></div>
                <p className="mt-6 text-sm font-bold uppercase tracking-[0.2em] text-emerald-700">Готово</p>
                <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">Документы отправлены</h1>
                <p className="mx-auto mt-4 max-w-md text-lg leading-8 text-slate-600">HR получил пакет и проверит его. Если потребуется уточнение, с вами свяжутся.</p>
                <div className="mt-7 rounded-2xl bg-slate-50 px-5 py-4 text-sm text-slate-500">Эту страницу можно безопасно закрыть.</div>
            </section>
        </main>;
    }
    if (!linkChecked) {
        return <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6"><div className="text-center"><div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-amber-500" /><p className="mt-5 text-lg font-medium text-slate-600">Проверяем персональную ссылку…</p></div></main>;
    }
    if (!info) {
        return <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-red-50 p-5"><section className="w-full max-w-lg rounded-[2rem] border border-red-100 bg-white p-8 text-center shadow-xl"><div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-2xl text-red-700">!</div><h1 className="mt-5 text-3xl font-bold text-slate-950">Ссылка недоступна</h1><p className="mt-3 text-base leading-7 text-red-700">{error || "Срок действия ссылки истёк или она уже была использована."}</p><button type="button" onClick={loadForm} className="mt-6 min-h-12 rounded-2xl bg-slate-950 px-6 py-3 font-semibold text-white transition hover:bg-slate-800">Попробовать снова</button></section></main>;
    }

    return (
        <main className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-amber-50 px-4 py-5 text-slate-900 md:px-8 md:py-10">
            <div className="mx-auto max-w-6xl">
                <header className="overflow-hidden rounded-[2rem] bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 px-6 py-8 text-white shadow-2xl shadow-slate-300/70 md:px-10 md:py-11">
                    <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                        <div className="max-w-3xl">
                            <p className="text-sm font-bold uppercase tracking-[0.22em] text-amber-300">Оформление на работу</p>
                            <h1 className="mt-3 text-3xl font-bold tracking-tight md:text-5xl">Документы для трудоустройства</h1>
                            <p className="mt-4 text-lg leading-8 text-slate-300"><b className="text-white">{info.candidate.full_name}</b>, загрузите читаемые фотографии или PDF. Черновик сохранится автоматически.</p>
                            {info.mode === "revision" ? <div className="mt-5 rounded-2xl border border-amber-300/40 bg-amber-300/10 px-4 py-3 text-sm leading-6 text-amber-100"><b className="block text-amber-300">HR просит дополнить пакет</b>{info.revision_reason || "Добавьте недостающие документы и отправьте пакет повторно."}</div> : null}
                        </div>
                        <div className="flex shrink-0 items-center gap-4 rounded-2xl border border-white/10 bg-white/10 px-5 py-4 backdrop-blur">
                            <PageMark />
                            <div><p className="text-sm text-slate-300">Загружено</p><p className="text-2xl font-bold">{entries.length} из {MAX_CANDIDATE_DOCUMENT_FILES}</p><p className="text-xs text-slate-400">{formatFileSize(totalBytes)}</p></div>
                        </div>
                    </div>
                </header>

                <div className="mt-7 grid items-start gap-7 lg:grid-cols-[minmax(0,1fr)_360px]">
                    <div className="space-y-7">
                        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/50 md:p-8">
                            <div className="flex items-start gap-4"><span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-lg font-bold text-white">1</span><div><h2 className="text-2xl font-bold">Выберите документ</h2><p className="mt-1 text-base text-slate-500">Укажите тип, затем добавьте одну или несколько страниц.</p></div></div>
                            <label className="mt-6 block text-sm font-bold uppercase tracking-wider text-slate-500" htmlFor="actual-residence-address">Фактический адрес проживания *</label>
                            <textarea id="actual-residence-address" aria-label="Фактический адрес проживания" required maxLength={2000} rows={3} value={actualResidenceAddress} onChange={(event) => setActualResidenceAddress(event.target.value)} placeholder="Индекс, регион, город, улица, дом, квартира" className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100" />
                            <p className="mt-2 text-sm text-slate-500">Укажите адрес, где вы проживаете сейчас. Он может отличаться от адреса регистрации в паспорте.</p>
                            <label className="mt-6 block text-sm font-bold uppercase tracking-wider text-slate-500" htmlFor="candidate-document-kind">Тип документа</label>
                            <select id="candidate-document-kind" aria-label="Тип документа" className="mt-2 min-h-14 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base font-medium outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100" value={kind} onChange={(event) => setKind(event.target.value)}>{CANDIDATE_DOCUMENT_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
                            {selectedDocument?.hint ? <p className="mt-2 text-sm text-slate-500">{selectedDocument.hint}</p> : null}
                            <label className={`mt-5 flex min-h-40 cursor-pointer flex-col items-center justify-center rounded-3xl border-2 border-dashed border-amber-300 bg-amber-50/60 px-6 py-7 text-center transition hover:border-amber-500 hover:bg-amber-50 ${uploading || submitting ? "pointer-events-none opacity-60" : ""}`}>
                                <span aria-hidden="true" className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-200 text-2xl text-amber-900">+</span>
                                <span className="mt-3 text-lg font-bold text-slate-950">{uploading ? "Загружаем файлы…" : "Выбрать файлы"}</span>
                                <span className="mt-1 text-sm text-slate-600">JPG, PNG, WEBP или PDF · до 10 МБ каждый</span>
                                <input aria-label="Добавить файлы" className="sr-only" type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" disabled={uploading || submitting} onChange={addFiles} />
                            </label>
                        </section>

                        {existingDocuments.length ? <section className="rounded-[2rem] border border-emerald-200 bg-emerald-50/50 p-6 md:p-8">
                            <h2 className="text-xl font-bold text-emerald-950">Ранее отправленные документы сохранены</h2>
                            <p className="mt-1 text-sm text-emerald-800">Повторно загружать их не нужно. Добавьте только документы, указанные HR.</p>
                            <div className="mt-5 grid gap-3 sm:grid-cols-2">{existingDocuments.map((item, index) => <a key={item.key || `${item.kind}-${index}`} href={item.url} target="_blank" rel="noreferrer" className="rounded-2xl border border-emerald-200 bg-white p-4 hover:border-emerald-400"><b className="block text-sm text-slate-900">{CANDIDATE_DOCUMENT_LABELS[item.kind] || item.kind}</b><span className="mt-1 block truncate text-xs text-slate-500">{item.filename}</span></a>)}</div>
                        </section> : null}

                        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/50 md:p-8">
                            <div className="flex items-start justify-between gap-4"><div className="flex items-start gap-4"><span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-lg font-bold text-white">2</span><div><h2 className="text-2xl font-bold">Проверьте загруженное</h2><p className="mt-1 text-base text-slate-500">Убедитесь, что названия и типы документов указаны верно.</p></div></div><span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-600">{entries.length}</span></div>
                            {entries.length ? <div className="mt-6 space-y-3">{entries.map((item) => <article key={item.file_id} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 transition hover:border-slate-300">
                                <span aria-hidden="true" className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-lg shadow-sm ${item.validation_status === "valid" ? "text-emerald-700" : item.validation_status === "invalid" ? "text-red-700" : "text-amber-700"}`}>
                                    {item.validation_status === "checking" || !item.validation_status
                                        ? <span className="h-5 w-5 animate-spin rounded-full border-2 border-amber-200 border-t-amber-600" />
                                        : item.validation_status === "valid" ? "✓" : "!"}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <h3 className="font-bold text-slate-900">{CANDIDATE_DOCUMENT_LABELS[item.kind] || item.kind}</h3>
                                    <p className="truncate text-sm text-slate-500">{item.filename} · {formatFileSize(item.size)}</p>
                                    <p className={`mt-1 text-sm font-medium ${item.validation_status === "valid" ? "text-emerald-700" : item.validation_status === "invalid" ? "text-red-700" : "text-amber-700"}`}>
                                        {item.validation_message || "Документ отправлен на проверку"}
                                    </p>
                                    {item.validation_status === "error" || item.validation_status === "pending" ? <button type="button" className="mt-1 text-sm font-bold text-amber-800 underline" onClick={() => validateFile(item, sessionToken)}>Проверить повторно</button> : null}
                                </div>
                                <button disabled={submitting || deletingIds.has(item.file_id)} type="button" className="min-h-10 rounded-xl px-3 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-50" onClick={() => removeFile(item)}>{deletingIds.has(item.file_id) ? "Удаляем…" : "Удалить"}</button>
                            </article>)}</div> : <div className="mt-6 rounded-3xl border-2 border-dashed border-slate-200 px-6 py-10 text-center"><p className="text-lg font-semibold text-slate-500">Пока ничего не загружено</p><p className="mt-1 text-sm text-slate-400">Добавленные файлы появятся здесь.</p></div>}
                            <div className="mt-5 flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-500"><span>Черновик хранится 72 часа и восстановится по этой ссылке.</span><b className="text-slate-700">Всего: {formatFileSize(totalBytes)}</b></div>
                        </section>

                        {error ? <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-5 text-base font-medium text-red-800">{error}</div> : null}
                    </div>

                    <aside className="space-y-5 lg:sticky lg:top-7">
                        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/50">
                            <h2 className="text-xl font-bold">Что нужно приложить</h2>
                            <p className="mt-2 text-sm leading-6 text-slate-500">Для одного документа можно загрузить несколько фотографий или страниц.</p>
                            <ul className="mt-5 space-y-2.5">{CANDIDATE_DOCUMENT_TYPES.filter(item => item.value !== "other").map(item => <li key={item.value} className="flex items-start gap-3 text-sm leading-5"><span aria-hidden="true" className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${uploadedKinds.has(item.value) ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{uploadedKinds.has(item.value) ? "✓" : "·"}</span><span className={uploadedKinds.has(item.value) ? "font-medium text-slate-900" : "text-slate-600"}>{item.label}{item.value === "bank_details" ? " — необязательно" : ""}</span></li>)}</ul>
                        </section>
                        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-950"><b className="block text-base">Сохраните оригиналы</b><span>Все документы необходимо принести с собой в оригинале в день трудоустройства.</span></div>
                    </aside>
                </div>

                <section className="sticky bottom-3 z-10 mt-7 flex flex-col gap-3 rounded-3xl border border-slate-200 bg-white/95 p-4 shadow-2xl backdrop-blur md:flex-row md:items-center md:p-5">
                    <div className="mr-auto"><p className="font-bold text-slate-900">Шаг 3. Отправьте документы HR</p><p className="text-sm text-slate-500">{missingRequiredKinds.length ? `Не хватает: ${missingRequiredKinds.map(item => CANDIDATE_DOCUMENT_LABELS[item] || item).join(", ")}` : checkingCount ? `Проверяем документов: ${checkingCount}` : allDocumentsValid ? "Все документы проверены и готовы к отправке." : entries.length ? "Исправьте документы с замечаниями или повторите проверку." : "После отправки изменить пакет самостоятельно будет нельзя."}</p></div>
                    <button aria-label="Отправить пакет документов" type="button" disabled={!allDocumentsValid || missingRequiredKinds.length > 0 || !actualResidenceAddress.trim() || busy} onClick={submit} className="min-h-14 rounded-2xl bg-amber-400 px-7 py-3 text-base font-bold text-slate-950 shadow-lg shadow-amber-200 transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none">{submitting ? "Отправляем пакет…" : checkingCount ? "Документы проверяются…" : missingRequiredKinds.length ? "Приложите обязательные документы" : allDocumentsValid && actualResidenceAddress.trim() ? `Отправить пакет · ${entries.length}` : !actualResidenceAddress.trim() ? "Укажите фактический адрес" : entries.length ? "Нужна проверка документов" : "Сначала добавьте документы"}</button>
                </section>
            </div>
        </main>
    );
}
